import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';

import { AppIcon, AppIconSize } from '@tablesready/frontend-icons';
import { DateTimeService } from '@tablesready/common-ui';

@Component({
  selector: 'tablesready-date-stepper',
  templateUrl: './date-stepper.component.html',
  styleUrls: ['./date-stepper.component.scss'],
  standalone: false,
})
export class DateStepperComponent implements OnInit, OnChanges {
  @Input() date: NgbDateStruct | null = null;
  @Input() minDate?: NgbDateStruct;
  @Input() maxDate?: NgbDateStruct;
  @Input() timezone?: string;
  @Input() defaultToToday = true;

  @Output() dateChange = new EventEmitter<NgbDateStruct>();

  readonly icon = AppIcon;
  readonly iconSize = AppIconSize;

  constructor(private readonly dateTimeService: DateTimeService) {}

  ngOnInit(): void {
    if (!this.date && this.defaultToToday) {
      this.applyToday();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['timezone'] && !this.date && this.defaultToToday) {
      this.applyToday();
    }
  }

  stepDate(delta: number): void {
    if (delta < 0 && this.isPrevDayDisabled()) {
      return;
    }
    if (delta > 0 && this.isNextDayDisabled()) {
      return;
    }

    const base = this.dateAsMoment() ?? moment.tz(this.timezone);
    const next = base.clone().add(delta, 'days');
    this.emit(this.dateTimeService.momentToNgbDate(next));
  }

  goToToday(): void {
    this.applyToday();
  }

  onModelChange(value: NgbDateStruct | null): void {
    if (value) {
      this.emit(value);
    }
  }

  isPrevDayDisabled(): boolean {
    if (!this.minDate) {
      return false;
    }
    return this.compareToBound(this.minDate) <= 0;
  }

  isNextDayDisabled(): boolean {
    if (!this.maxDate) {
      return false;
    }
    return this.compareToBound(this.maxDate) >= 0;
  }

  private applyToday(): void {
    const today = moment.tz(this.timezone);
    this.emit(this.dateTimeService.momentToNgbDate(today));
  }

  private emit(value: NgbDateStruct): void {
    this.date = value;
    this.dateChange.emit(value);
  }

  private dateAsMoment(): moment.Moment | undefined {
    if (!this.date) {
      return undefined;
    }
    return this.dateTimeService.ngbDateToMoment(this.date, this.timezone);
  }

  private compareToBound(bound: NgbDateStruct): number {
    const current = this.dateAsMoment();
    if (!current) {
      return 0;
    }
    const boundMs = this.dateTimeService.ngbDateToMoment(bound, this.timezone).valueOf();
    return current.valueOf() - boundMs;
  }
}
